// Theme mode state: resolves the active light/dark mode and provides the
// MUI theme for it. Defaults to the user's system preference
// (prefers-color-scheme) and keeps following it until the user explicitly
// toggles, at which point the choice is persisted in localStorage and wins
// over the system setting on future visits.
import {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { buildTheme } from './theme.js';

const STORAGE_KEY = 'theme-mode'; // 'light' | 'dark'; absent = follow system

const ThemeModeContext = createContext({ mode: 'dark', toggleMode: () => {} });

export const useThemeMode = () => useContext(ThemeModeContext);

const systemQuery = () => window.matchMedia?.('(prefers-color-scheme: dark)');

const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null;
  }
};

const systemMode = () => (systemQuery()?.matches !== false ? 'dark' : 'light');

const ThemeModeProvider = ({ children }) => {
  const [preference, setPreference] = useState(readStoredMode);
  const [system, setSystem] = useState(systemMode);

  // Track OS-level changes so un-toggled users follow their system live.
  useEffect(() => {
    const query = systemQuery();
    if (!query) return undefined;
    const onChange = (event) => setSystem(event.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const mode = preference ?? system;

  // Admin-edited palette overrides (the `theme_palette` portal setting) ride
  // the public config; buildTheme falls back to built-ins per missing key.
  const themePalette = useSelector((s) => s.settings.settings.theme_palette);

  const value = useMemo(() => ({
    mode,
    toggleMode: () => {
      const next = mode === 'dark' ? 'light' : 'dark';
      setPreference(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Storage unavailable (private mode); the toggle still works in-session.
      }
    },
  }), [mode]);

  const theme = useMemo(
    () => buildTheme(mode, themePalette?.[mode] ?? null),
    [mode, themePalette],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeProvider;
